import { type ComponentProps, useId } from "react";
import { contextRight, contextWidth } from "../brand/context-line";
import { contextGlyphs, lockup, products, symbol, tag } from "../brand/logo-paths";
import { cn } from "../cn";

// The Nexxt logo (ADR-072), the only place the apps take it from (brand/README.md §8). The outlines are generated by
// `pnpm brand:build`; here they are only laid out. One colour, the colour of the text around it.

type LogoVariant = "lockup" | "compact" | "wordmark" | "tag" | "symbol";

/** A product of the suite (ADR-102), by the id of its app: the one list is the generated one. */
export type Product = keyof typeof products;

interface LogoProps extends Omit<ComponentProps<"svg">, "children" | "viewBox"> {
  /** Which one where: brand/README.md §7. */
  variant: LogoVariant;
  /** Right side of the context line of `lockup` and `tag`: the environment, ten letters or digits at most. */
  context?: string;
  /** For `lockup` alone: the line of the product under the barcode, and the name the logo says. */
  product?: Product;
}

// The name is the brand, the same in every language; an app says its product after it (ADR-102).
const NAME = "Nexxt";
const nameOf = (product?: Product) => (product ? `${NAME} ${products[product].name}` : NAME);

const viewBox: Record<LogoVariant, string> = {
  lockup: `0 0 ${lockup.w} ${lockup.h}`,
  compact: `0 0 ${lockup.w} ${lockup.compactH}`,
  wordmark: `0 0 ${lockup.w} ${lockup.wordmarkH}`,
  tag: `0 0 ${tag.w} ${tag.h}`,
  symbol: `0 0 ${symbol.box} ${symbol.box}`,
};

/** Set the width with a class; the height follows. With `aria-hidden` it is decoration next to a name already said. */
export function Logo({ variant, context = "B2B", product, className, ...props }: LogoProps) {
  const line = variant === "lockup" && product ? products[product].line : null;
  return (
    <svg
      role="img"
      aria-label={line ? nameOf(product) : NAME}
      viewBox={line ? `0 0 ${lockup.w} ${lockup.productH}` : viewBox[variant]}
      fill="currentColor"
      className={cn("h-auto shrink-0", className)}
      {...props}
    >
      {variant === "symbol" ? (
        <SymbolArt />
      ) : variant === "tag" ? (
        <TagArt context={context} />
      ) : (
        <>
          <path d={lockup.wordmark} />
          {variant !== "wordmark" && <path d={lockup.bars} transform={`translate(0 ${lockup.barsY})`} />}
          {line && <path d={line} transform={`translate(0 ${lockup.productBaseline})`} />}
          {variant === "lockup" && (
            <ContextLine
              left={lockup.contextLeft}
              right={context}
              width={lockup.w}
              y={line ? lockup.productContextBaseline : lockup.contextBaseline}
            />
          )}
        </>
      )}
    </svg>
  );
}

interface LogoMarkProps {
  /** Which app this is: its name follows the wordmark. */
  product: Product;
  className?: string;
  /** Hides the name where the bar has no room for it: the symbol stays, and still says the name. */
  nameClassName?: string;
  /** Hides the product: it goes before the name does. */
  productClassName?: string;
}

/** Symbol, name and product, for the bar of every app (brand/README.md §8): one image for assistive technology. */
export function LogoMark({ product, className, nameClassName, productClassName }: LogoMarkProps) {
  return (
    <div role="img" aria-label={nameOf(product)} className={cn("flex items-center gap-2", className)}>
      <Logo variant="symbol" aria-hidden className="size-6" />
      {/* Outlines have no hinting: on a 1x screen an edge half a pixel off is a grey line two pixels wide. The name
          is sized by an even height, so in the 24 px row its cap height and baseline fall on whole pixels. */}
      <div className="flex items-end gap-1.5">
        <Logo variant="wordmark" aria-hidden className={cn("h-3 w-auto", nameClassName)} />
        <ProductName product={product} className={productClassName} />
      </div>
    </div>
  );
}

// The product in the bar: the letters of the context line, smaller and lighter than the name they follow. The box is
// the cap height, on the name's baseline, so both its edges fall on whole pixels too; the round letters go a little
// past it, as in text.
function ProductName({ product, className }: { product: Product; className: string | undefined }) {
  const { name } = products[product];
  const width = contextWidth(name);
  const { capHeight } = contextGlyphs;
  return (
    <svg
      aria-hidden
      viewBox={`0 ${-capHeight} ${width} ${capHeight}`}
      fill="currentColor"
      className={cn("h-2 w-auto shrink-0 overflow-visible opacity-70", className)}
    >
      {contextRight(name, width).map((glyph) => (
        <path key={glyph.x} d={glyph.d} transform={`translate(${glyph.x} 0)`} />
      ))}
    </svg>
  );
}

interface ContextLineProps {
  left: string;
  right: string;
  width: number;
  x?: number;
  /** The baseline of the line. */
  y: number;
}

function ContextLine({ left, right, width, x = 0, y }: ContextLineProps) {
  return (
    <g transform={`translate(${x} ${y})`} opacity="0.7">
      <path d={left} />
      {contextRight(right, width).map((glyph) => (
        <path key={glyph.x} d={glyph.d} transform={`translate(${glyph.x} 0)`} />
      ))}
    </g>
  );
}

function TagArt({ context }: { context: string }) {
  return (
    <>
      <rect x="1" y="1" width={tag.w - 2} height={tag.h - 2} rx="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="16" cy="16" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d={tag.word} transform={`translate(${tag.wordX} ${tag.wordY})`} />
      <ContextLine
        left={tag.contextLeft}
        right={context}
        width={tag.contextW}
        x={tag.contextX}
        y={tag.contextBaseline}
      />
    </>
  );
}

// The letters are holes in the square, so the symbol works on any background without knowing its colour.
function SymbolArt() {
  const mask = useId();
  return (
    <>
      <mask id={mask}>
        <rect width={symbol.box} height={symbol.box} fill="#fff" />
        <path d={symbol.letters} fill="#000" />
      </mask>
      <rect width={symbol.box} height={symbol.box} rx={symbol.radius} mask={`url(#${mask})`} />
    </>
  );
}
